import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Key, Copy, Trash2, RefreshCw, Check, ExternalLink } from "lucide-react";

export default function ApiKeyCard() {
  const [status, setStatus] = useState({ loading: true });
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [newKey, setNewKey] = useState(null); // shown once after generation
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/auth/api-key");
      setStatus({ ...data, loading: false });
    } catch {
      setStatus({ has_key: false, loading: false });
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/auth/api-key");
      setNewKey(data.api_key);
      setStatus({ has_key: true, last_four: data.api_key.slice(-4), prefix: "pb_", created_at: data.created_at });
      toast.success("API key generated — copy it now, it won't be shown again");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate key");
    }
    setGenerating(false);
  };

  const revoke = async () => {
    if (!window.confirm("Revoke your API key? Hermes and any other tools using it will lose access.")) return;
    setRevoking(true);
    try {
      await api.delete("/auth/api-key");
      setStatus({ has_key: false });
      setNewKey(null);
      toast.success("API key revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke key");
    }
    setRevoking(false);
  };

  const copyKey = async () => {
    if (!newKey) return;
    await navigator.clipboard.writeText(newKey);
    setCopied(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopied(false), 3000);
  };

  if (status.loading) return null;

  return (
    <div className="card-surface p-8 space-y-5" data-testid="api-key-card">
      <div className="flex items-center gap-3">
        <Key size={20} className="text-[#38BDF8]" />
        <div>
          <h2 className="font-display text-xl uppercase tracking-tight font-black">API Key</h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            Connect Hermes Agent or other tools to manage your coaching business without opening the app.
          </p>
        </div>
      </div>

      {/* New key reveal — shown once */}
      {newKey && (
        <div className="bg-[#38BDF8]/10 border border-[#38BDF8]/30 p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-[#38BDF8] font-bold">Your API key — copy it now</div>
          <div className="flex items-center gap-2">
            <code className="bg-black/30 px-3 py-2 text-sm text-[#38BDF8] font-mono break-all flex-1 select-all">
              {newKey}
            </code>
            <button
              onClick={copyKey}
              className="p-2 hover:bg-white/10 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
            </button>
          </div>
          <div className="text-xs text-[#A3A3A3] space-y-1">
            <p>Add this to your Hermes environment:</p>
            <code className="block bg-black/30 px-3 py-2 text-[11px] text-[#A3A3A3] font-mono break-all">
              echo 'PBCOACH_API_KEY={newKey}' &gt;&gt; ~/.hermes/.env
            </code>
            <p className="mt-2">
              Then install the Hermes skill:{" "}
              <code className="bg-black/30 px-1.5 py-0.5 text-[11px] font-mono">hermes skills install pbcoach-app</code>
            </p>
          </div>
        </div>
      )}

      {/* Key status */}
      {!newKey && status.has_key && (
        <div className="flex items-center justify-between bg-white/5 border border-white/10 p-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-1">Active key</div>
            <code className="text-sm font-mono text-[#A3A3A3]">
              {status.prefix}••••••••••••••••••••••••{status.last_four}
            </code>
            {status.created_at && (
              <div className="text-[10px] text-[#666] mt-1">
                Created {new Date(status.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={generate}
              disabled={generating}
              className="text-xs px-3 py-1.5 border border-white/10 hover:border-[#38BDF8]/50 transition-colors inline-flex items-center gap-1.5"
              title="Generate new key (revokes old one)"
            >
              <RefreshCw size={12} className={generating ? "animate-spin" : ""} />
              Rotate
            </button>
            <button
              onClick={revoke}
              disabled={revoking}
              className="text-xs px-3 py-1.5 border border-red-500/30 hover:border-red-500 hover:text-red-400 transition-colors inline-flex items-center gap-1.5"
            >
              <Trash2 size={12} />
              Revoke
            </button>
          </div>
        </div>
      )}

      {/* No key yet */}
      {!newKey && !status.has_key && (
        <div className="bg-white/5 border border-dashed border-white/10 p-6 text-center space-y-3">
          <p className="text-sm text-[#A3A3A3]">
            Generate an API key to let Hermes Agent manage your lessons, notes, progress, and messages — without opening the app.
          </p>
          <button
            onClick={generate}
            disabled={generating}
            className="btn-primary py-2.5 px-6 text-xs inline-flex items-center gap-2"
          >
            <Key size={14} />
            {generating ? "Generating…" : "Generate API key"}
          </button>
        </div>
      )}

      <div className="text-[10px] text-[#666] flex items-center gap-1">
        <ExternalLink size={10} />
        <span>Install the </span>
        <a href="https://hermes-agent.nousresearch.com" target="_blank" rel="noopener noreferrer" className="text-[#38BDF8] hover:underline">
          Hermes Agent
        </a>
        <span> skill: </span>
        <code className="bg-white/5 px-1 py-0.5 font-mono">hermes skills install pbcoach-app</code>
      </div>
    </div>
  );
}
